import { useCallback, useEffect, useRef } from "react";
import { appState } from "../../state";
import { requestLock } from "../../lockBus";
import { showToast } from "../../toast";

const AUTOLOCK_ON_TAB_CHANGE = __APP_CONFIG__.autolockOnTabChange;
const AUTOLOCK_ON_SCREEN_OFF = __APP_CONFIG__.autolockOnScreenOff;
const AUTOLOCK_MINUTES = __APP_CONFIG__.autolockMinutes;

const WARNING_SECONDS_BEFORE = 15;
const BLUR_GRACE_MS = 4000;

function isLockedNow(): boolean {
  return document.getElementById("contentWrap")?.classList.contains("fade-lock") ?? false;
}

// Port of autolock.ts's timer choreography. The original guarded listener
// attachment behind `if (appState.autolockArmed) return` inside
// startAutolockWatchers() so it only ever ran once per page load (vanilla
// has no mount/unmount lifecycle to hook into) - here that's just a
// useEffect with an empty dep array, attached once for the life of the
// page by whichever component calls this hook (AuthGate.tsx). Arming/
// disarming (appState.autolockArmed) stays a separate concern, toggled by
// armAutolock()/disarmAutolock() on every unlock/lock, same as the
// original's startAutolockWatchers()/stopAutolockWatchers() calls from
// setUnlocked().
export function useAutolock() {
  const warningTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurGraceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const shouldIgnoreInstantLock = useCallback((): boolean => {
    return appState.filePickerOpen || appState.uploadInProgress;
  }, []);

  const clearScheduledLock = useCallback((): void => {
    if (appState.inactivityTimer) {
      clearTimeout(appState.inactivityTimer);
      appState.inactivityTimer = null;
    }
    if (warningTimer.current) {
      clearTimeout(warningTimer.current);
      warningTimer.current = null;
    }
  }, []);

  const resetInactivityTimer = useCallback((): void => {
    if (!appState.autolockArmed) return;
    if (shouldIgnoreInstantLock()) return;
    if (appState.mediaPlaying) return; // resumes when playback stops

    clearScheduledLock();
    if (!(AUTOLOCK_MINUTES > 0)) return;

    const totalMs = AUTOLOCK_MINUTES * 60 * 1000;
    const warnDelayMs = Math.max(0, totalMs - WARNING_SECONDS_BEFORE * 1000);

    if (warnDelayMs > 0) {
      warningTimer.current = setTimeout(() => {
        warningTimer.current = null;
        if (isLockedNow() || shouldIgnoreInstantLock() || appState.mediaPlaying) return;
        showToast(`Locking in ${WARNING_SECONDS_BEFORE}s due to inactivity`, "info", WARNING_SECONDS_BEFORE * 1000);
      }, warnDelayMs);
    }

    appState.inactivityTimer = setTimeout(() => {
      appState.inactivityTimer = null;
      if (isLockedNow() || shouldIgnoreInstantLock() || appState.mediaPlaying) return;
      requestLock("inactivity");
    }, totalMs);
  }, [shouldIgnoreInstantLock, clearScheduledLock]);

  const pauseInactivityForMedia = useCallback((): void => {
    appState.mediaPlaying = true;
    clearScheduledLock();
  }, [clearScheduledLock]);

  const resumeInactivityAfterMedia = useCallback((): void => {
    appState.mediaPlaying = false;
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const armAutolock = useCallback((): void => {
    appState.autolockArmed = true;
    resetInactivityTimer();
  }, [resetInactivityTimer]);

  const disarmAutolock = useCallback((): void => {
    appState.autolockArmed = false;
    clearScheduledLock();
    if (blurGraceTimer.current) {
      clearTimeout(blurGraceTimer.current);
      blurGraceTimer.current = null;
    }
  }, [clearScheduledLock]);

  useEffect(() => {
    const activityEvents = ["mousemove", "mousedown", "keydown", "touchstart", "touchmove", "scroll", "wheel", "pointerdown"];
    activityEvents.forEach((evt) => {
      window.addEventListener(evt, resetInactivityTimer, { passive: true });
    });

    const onBlur = () => {
      if (!AUTOLOCK_ON_TAB_CHANGE) return;
      if (isLockedNow() || shouldIgnoreInstantLock()) return;

      if (blurGraceTimer.current) clearTimeout(blurGraceTimer.current);
      blurGraceTimer.current = setTimeout(() => {
        blurGraceTimer.current = null;
        if (isLockedNow() || shouldIgnoreInstantLock()) return;
        requestLock("blur");
      }, BLUR_GRACE_MS);
    };
    const onFocus = () => {
      if (blurGraceTimer.current) {
        clearTimeout(blurGraceTimer.current);
        blurGraceTimer.current = null;
      }
    };
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);

    const onVisibilityChange = () => {
      if (!AUTOLOCK_ON_SCREEN_OFF) return;
      if (!document.hidden) return;
      if (isLockedNow() || shouldIgnoreInstantLock()) return;
      requestLock("hidden");
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const onPagehide = () => {
      if (!AUTOLOCK_ON_SCREEN_OFF) return;
      if (isLockedNow() || shouldIgnoreInstantLock()) return;
      requestLock("pagehide");
    };
    window.addEventListener("pagehide", onPagehide);

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, resetInactivityTimer));
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPagehide);
    };
  }, [resetInactivityTimer, shouldIgnoreInstantLock]);

  return { resetInactivityTimer, pauseInactivityForMedia, resumeInactivityAfterMedia, armAutolock, disarmAutolock };
}
